import type { ContentLang } from '@/features/editor/editorSlice'
import type { TabKey } from '@/features/editor/components/course-settings/tabs'
import {
  fieldLabel,
  partLabel,
  type FieldRef,
  type ViolationTarget,
} from '@/features/moderation/lib/violationRef'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export type ViolationGo =
  /** `lang` — открыть блок на языке, где не хватает текста или картинки. */
  | { kind: 'block'; lessonId: number; blockId: number; lang?: ContentLang }
  /** `lang` — переключить язык правки, чтобы панель урока открылась на этом языке. */
  | { kind: 'lesson'; lessonId: number; lang?: ContentLang }
  /**
   * `lang` — открыть поля на этом языке (незаполненное поле EN); `tab` — вкладка
   * настроек, где лежит поле (по умолчанию «Основное»).
   */
  | { kind: 'course'; lang?: ContentLang; tab?: TabKey }
export interface ViolationPlace {
  label: string
  go: ViolationGo | null
}

/** Вкладка настроек, где автор правит поле курса. `Map` — у объекта есть прототип. */
const COURSE_FIELD_TAB: ReadonlyMap<string, TabKey> = new Map([
  ['banner', 'basic'],
  ['style_references', 'design'],
])

const join = (parts: (string | null)[]): string => parts.filter(Boolean).join(' · ')
const withField = (head: string, f: FieldRef | null): string =>
  join([head, f ? fieldLabel(f) : null])

type BlockTarget = Extract<ViolationTarget, { kind: 'block' }>

/** Язык поля нарушения: колонка `*_en` — английский, без суффикса — русский. */
const langOf = (f: FieldRef | null): ContentLang | undefined =>
  f ? (f.lang === 'en' ? 'en' : 'ru') : undefined

const blockPlace = (
  t: BlockTarget,
  lessonNo: string,
  lessonBlocks: { id: number }[] | undefined,
): ViolationPlace => {
  const m = messagesNow(messages)
  const lang = langOf(t.field)
  const lessonGo: ViolationGo = { kind: 'lesson', lessonId: t.lessonId, lang }
  const blockGo: ViolationGo = { kind: 'block', lessonId: t.lessonId, blockId: t.blockId, lang }
  if (!lessonBlocks) return { label: m.screen(lessonNo), go: blockGo }
  const idx = lessonBlocks.findIndex((b) => b.id === t.blockId)
  if (idx === -1) return { label: m.screenDeleted(lessonNo), go: lessonGo }
  const tail = join([t.part ? partLabel(t.part) : null, t.field ? fieldLabel(t.field) : null])
  return { label: join([m.screenN(lessonNo, idx + 1), tail || null]), go: blockGo }
}

/** Позиционные номера (как на миниатюрах, не `order`) и признак «удалён». */
export const violationPlace = (
  t: ViolationTarget,
  lessons: { id: number }[],
  lessonBlocks: { id: number }[] | undefined,
): ViolationPlace => {
  const m = messagesNow(messages)
  if (t.kind === 'image') return { label: m.image, go: null }
  if (t.kind === 'unknown') return { label: t.raw, go: null }
  // Нарушение статьи в заявке курса не приходит; своей точки перехода у него нет.
  if (t.kind === 'article') return { label: withField(m.article, t.field), go: null }
  if (t.kind === 'mascot')
    return { label: join([m.course, m.mascot]), go: { kind: 'course', tab: 'map' } }
  if (t.kind === 'course') {
    const tab = t.field ? COURSE_FIELD_TAB.get(t.field.name) : undefined
    const go: ViolationGo = { kind: 'course', lang: langOf(t.field), ...(tab ? { tab } : {}) }
    return { label: withField(m.course, t.field), go }
  }
  const idx = lessons.findIndex((l) => l.id === t.lessonId)
  const lessonNo = idx === -1 ? m.lessonDeleted : m.lessonN(idx + 1)
  if (t.kind === 'lesson') {
    return {
      label: withField(lessonNo, t.field),
      go: idx === -1 ? null : { kind: 'lesson', lessonId: t.lessonId, lang: langOf(t.field) },
    }
  }
  if (idx === -1) return { label: lessonNo, go: null }
  return blockPlace(t, lessonNo, lessonBlocks)
}
